import React from 'react'
import Box from '@mui/material/Box'
import { useTranslation } from 'next-i18next'
import { EmtIcon, EmtTypography, useMobile } from 'emt-components-v2'
import { Skeleton } from '@mui/material'
import { usePetsitterUser } from '@/modules/PetsitterUser/hooks/usePetsitterUser'
import { Routes } from '@/assets/configs'
import { useRouter } from 'next/router'
import { appendParamsIfNeeded } from '@/utils'

export type StatsData = {
  recurringCustomersCount: number
  completedServicesCount: number
  averageResponseTime: string
}

type Props = {
  statsData?: StatsData
  averageReviewToRating?: number
  reviewsToCount?: number
  onScrollToSection?: (sectionId: string) => void
  isReviewsLoading?: boolean
  showZeroStats?: boolean
}

const Stats: React.FC<Props> = ({
  statsData,
  averageReviewToRating,
  reviewsToCount,
  onScrollToSection,
  isReviewsLoading,
  showZeroStats = true,
}) => {
  const { t } = useTranslation('petsitter')
  const [isMobile] = useMobile()
  const { petsitter } = usePetsitterUser()
  const router = useRouter()

  const reviewsCount = reviewsToCount ?? 0
  const recurringCustomersCount = statsData?.recurringCustomersCount ?? 0
  const completedServicesCount = statsData?.completedServicesCount ?? 0
  const shouldShowReviewsStat = showZeroStats || reviewsCount > 0
  const shouldShowRecurringCustomersStat = showZeroStats || recurringCustomersCount > 0
  const shouldShowCompletedServicesStat = showZeroStats || completedServicesCount > 0
  const shouldShowAverageResponseTimeStat = Boolean(statsData?.averageResponseTime)

  return (
    <Box display="flex" flexDirection="row" gap="20px" flexWrap="wrap">
      {!isMobile ? (
        <>
          <Box
            display={isMobile ? 'grid' : 'flex'}
            gridTemplateColumns={'1fr auto'}
            flexDirection="row"
            gap="20px"
            flexWrap="wrap"
          >
            {shouldShowReviewsStat &&
              (isReviewsLoading ? (
                <Skeleton variant="rectangular" sx={{ borderRadius: '5px', height: '100%', width: '72.5px' }} />
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="0px"
                  onClick={() => onScrollToSection?.('reviews')}
                  sx={{ cursor: 'pointer' }}
                >
                  <EmtTypography variant="body2" color="#4A4754">
                    {t('profile.stats.reviewsCount', { count: reviewsCount })}
                  </EmtTypography>
                  <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                    <EmtIcon className="emt-star" size={20} sx={{ color: '#FFDE54' }} />
                    <EmtTypography variant="body1" fontWeight={700}>
                      {averageReviewToRating ?? 5} / 5
                    </EmtTypography>
                  </Box>
                </Box>
              ))}
            {shouldShowRecurringCustomersStat && (
              <Box display="flex" flexDirection="column" gap="0px">
                <EmtTypography variant="body2" color="#4A4754">
                  {t('profile.stats.recurringCustomersPlural')}
                </EmtTypography>
                <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                  <EmtIcon className="emt-reload-user" size={20} sx={{ color: '#AE59C6' }} />
                  <EmtTypography variant="body1" fontWeight={700}>
                    {recurringCustomersCount > 1
                      ? t('profile.stats.recurringCustomersCountPlural', {
                          count: recurringCustomersCount,
                        })
                      : t('profile.stats.recurringCustomersCount', { count: recurringCustomersCount })}
                  </EmtTypography>
                </Box>
              </Box>
            )}
            {shouldShowAverageResponseTimeStat && (
              <Box display="flex" flexDirection="column" gap="0px">
                <EmtTypography variant="body2" color="#4A4754">
                  {t('profile.stats.averageResponseTimeLabel')}
                </EmtTypography>
                <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                  <EmtIcon className="emt-chat-1" size={20} color="#4A4754" />
                  <EmtTypography variant="body1" fontWeight={700}>
                    {statsData?.averageResponseTime}
                  </EmtTypography>
                </Box>
              </Box>
            )}
            {shouldShowCompletedServicesStat && (
              <Box display="flex" flexDirection="column" gap="0px">
                <EmtTypography variant="body2" color="#4A4754">
                  {t('profile.stats.completedServicesPlural')}
                </EmtTypography>
                <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                  <EmtIcon className="emt-shine" size={20} sx={{ color: '#AE59C6' }} />
                  <EmtTypography variant="body1" fontWeight={700}>
                    {completedServicesCount > 1
                      ? t('profile.stats.completedServicesCountPlural', { count: completedServicesCount })
                      : t('profile.stats.completedServicesCount', { count: completedServicesCount })}
                  </EmtTypography>
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box display={'grid'} gridTemplateColumns={'1fr 1fr 1fr 1fr'} gap="5px" sx={{ width: '100%' }}>
            {shouldShowReviewsStat &&
              (isReviewsLoading ? (
                <Skeleton variant="rectangular" sx={{ borderRadius: '15px', height: '100%', width: '100%' }} />
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  gap="0px"
                  onClick={() => {
                    !!petsitter && router.pathname === Routes.Pro.Dashboard
                      ? router.push(appendParamsIfNeeded(Routes.Pro.MyProfile + '?type=pro#reviews'))
                      : onScrollToSection?.('reviews')
                  }}
                  sx={{
                    border: '1px solid #E6E6E6',
                    borderRadius: '8px',
                    padding: '12px 5px',
                    width: '100%',
                    backgroundColor: '#FFF',
                  }}
                >
                  <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                    <EmtTypography variant="body1" fontWeight={800}>
                      {averageReviewToRating ?? 5}
                    </EmtTypography>
                    <EmtIcon className="emt-star" size={20} sx={{ color: '#FFDE54' }} />
                  </Box>
                  <EmtTypography variant="caption" color="#4A4754">
                    {t('profile.stats.reviewsCount', { count: reviewsCount })}
                  </EmtTypography>
                  <EmtIcon className="emt-down-1" size={20} />
                </Box>
              ))}
            {shouldShowAverageResponseTimeStat && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                gap="0px"
                sx={{
                  border: '1px solid #E6E6E6',
                  borderRadius: '8px',
                  padding: '12px 5px',
                  width: '100%',
                  backgroundColor: '#FFF',
                }}
              >
                <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                  <EmtTypography variant="body1" fontWeight={800}>
                    {statsData?.averageResponseTime}
                  </EmtTypography>
                </Box>
                <EmtTypography variant="caption" color="#4A4754">
                  {t('profile.stats.averageResponseTimeLabel')}
                </EmtTypography>
              </Box>
            )}
            {shouldShowRecurringCustomersStat && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                gap="0px"
                sx={{
                  border: '1px solid #E6E6E6',
                  borderRadius: '8px',
                  padding: '12px 5px',
                  width: '100%',
                  backgroundColor: '#FFF',
                }}
              >
                <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                  <EmtTypography variant="body1" fontWeight={800}>
                    {recurringCustomersCount}
                  </EmtTypography>
                  <EmtIcon className="emt-reload-user" size={20} sx={{ color: '#AE59C6' }} />
                </Box>
                <EmtTypography variant="caption" color="#4A4754">
                  {recurringCustomersCount > 1
                    ? t('profile.stats.recurringCustomersPlural')
                    : t('profile.stats.recurringCustomers')}
                </EmtTypography>
              </Box>
            )}
            {shouldShowCompletedServicesStat && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                gap="0px"
                sx={{
                  border: '1px solid #E6E6E6',
                  borderRadius: '8px',
                  padding: '12px 5px',
                  width: '100%',
                  backgroundColor: '#FFF',
                }}
              >
                <Box display="flex" flexDirection="row" gap="5px" alignItems="center">
                  <EmtTypography variant="body1" fontWeight={800}>
                    {completedServicesCount}
                  </EmtTypography>
                  <EmtIcon className="emt-shine" size={20} sx={{ color: '#AE59C6' }} />
                </Box>
                <EmtTypography variant="caption" color="#4A4754">
                  {completedServicesCount > 1
                    ? t('profile.stats.completedServicesPlural')
                    : t('profile.stats.completedServices')}
                </EmtTypography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default Stats
